import Head from "next/head";
import Legality from "@components/Legality";
import { BASE_ASSETS_URL } from "@constants/constants";
import {
  BuilderShowCaseSection,
  ShowCaseCard,
} from "components/CLM/BuilderShowCase";
import CLMHero2 from "components/CLM/CLMHero2";
import {
  CLMButtonPrimary,
  CLMButtonSecondary,
} from "components/CLM/v3/Buttons";
import { CheckIcon, Recycle, Rocket } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { IoPeople } from "react-icons/io5";
import { MdOutlinePeopleAlt } from "react-icons/md";
import { BasicChip } from "@components/MiniComponents";
import { BasicChipCertification } from "@constants/certifications";

const CLMHeroData = {
  // title: "Reporting",
  heading: "Integrated eSignatures built for contract precision",
  description:
    "From drafting to signing—keep every step secure, compliant, and within one unified platform.",
  image: `${BASE_ASSETS_URL}/v2/clm/clm/signature-hero.gif`,
  // logo: `${BASE_ASSETS_URL}/v2/clm/clm/ai-assistant-logo.png"),
  btnText: "Request a Demo",
  btnUrl: "/clm/request-demo",
};

const signatureReportingData = [
  {
    icon: Recycle,
    text: "Save time by automating routine tasks for scaling your small business.",
  },
  {
    icon: Rocket,
    text: "Speed up your business contracts for quicker decisions and better agility.",
  },
  {
    icon: MdOutlinePeopleAlt,
    text: "Facilitate better teamwork with features designed for easy collaboration.",
  },
];

const showcaseData = [
  {
    heading: "Upload your business contracts and get it signed in a few clicks",
    subHeading:
      "Get your PDFs signed in a secure and professional way to manage agreements with clients, suppliers, and employees.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/esignature-upload-1.png`,
  },
  {
    heading:
      "Customize PDF templates and reuse them anytime to meet your business needs",
    subHeading:
      "Assign roles and reuse the pre-made templates that you created to save time and avoid repetitive tasks.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/esignature-customize-1.png`,
  },
  {
    heading:
      "Collect important data and get signatures on forms that ensure compliance",
    subHeading:
      "Easily generate forms like registrations and purchase orders — all with secure, legally binding signatures.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/esignature-collect-1.png`,
  },
];

const showcaseData2 = [
  {
    heading: "Automate contracts with reusable templates",
    subHeading:
      "Convert any document into a smart template. Let your team auto-fill details like names and dates to create accurate contracts in just a few clicks.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/esignature-automate.png`,
  },
  {
    heading: "Simplify online terms management with BoloSign",
    subHeading:
      "Let your legal team edit, organize, and publish your website’s terms and policies in one secure, easy-to-use workspace.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/esignature-simplify.png`,
  },
  {
    heading: "Close contracts faster with BoloSign integrations",
    subHeading:
      "Connect your existing tools to create, track, and sign contracts with AI-powered speed and precision.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/esignature-close.png`,
  },
];

const Signature = ({ isDarkMode, setIsDarkMode }) => {
  const categories = ["PDFs", "PDFs Templates", "Forms"];
  const cardRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const topTrigger = 120; // or some margin like 100
      const bottomTrigger = window.innerHeight;

      const isInView =
        rect.top <= topTrigger && rect.bottom >= bottomTrigger / 2;

      setIsDarkMode(isInView);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToCard = (index) => {
    if (cardRefs.current[index]) {
      cardRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setActiveIndex(index);
    }
  };

  return (
    <div>
      <Head>
        <title>eSignatures - BoloSign CLM</title>
        <meta
          name="description"
          content="Secure, legally binding eSignatures built for contracts—only with BoloSign CLM."
        />
        <meta
          name="keywords"
          content="BoloSign eSignature, digital signatures, electronic signatures, contract signing, secure eSignatures, legally binding signatures, contract lifecycle management, CLM eSignatures"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/signature/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />

      <section className="bg-dark text-white py-12 px-6 md:px-8">
        <h2 className="text-4xl md:text-5xl mb-14 text-center">
          Affordable eSignature solution for your team
        </h2>
        <div className="max-w-screen-xl xl:px-8 mx-auto flex flex-col md:flex-row gap-6 justify-between items-center">
          {signatureReportingData.map((item, index) => (
            <div
              key={index}
              className="text-start flex flex-col items-center gap-3"
            >
              <item.icon className="text-white" size={48} />
              <div className="bg-[#2A2A2A] p-10 px-12 rounded-xl">
                <p className="text-2xl">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* <section className="px-0 md:px-8  bg-cream">
        <div className=" max-w-screen-xl mx-auto px-6 xl:px-8 md:py-32 py-16 text-black md:rounded-2xl flex flex-col md:flex-row gap-6 md:gap-12 justify-between ">
          <div className="w-full flex-1  mb-6 md:mb-0 flex flex-col xl:gap-8 relative">
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-semibold md:mb-2 xl:mb-8 xl:mt-4">
              eSignature laws are the same as handwritten ones
            </h2>
            <p className="text-lg md:text-2xl xl:text-3xl mb-8 md:w-2/3 pt-3 md:pt-0">
              Over 60 international laws* ensure the validity and legal effect
              of eSignatures
            </p>
            <div className="">
              <Link href="/clm/esignatures">
                <button className="px-10 py-3 bg-black text-white font-bold text-2xl rounded-lg">
                  Learn More
                </button>
              </Link>
            </div>

            <p className=" mt-8  border-t-4 border-gray-400 pt-4 absolute bottom-0 hidden lg:block text-sm">
              ESIGN Act Sec 106 (US federal law), GPEA Sec 1710 (US federal
              law), UETA Sec 2 (US state law), eIDAS regulation (European
              Union), Electronic Transactions Act 1999 (Australia), s.7
              Electronic Communications Act 2000 (UK), Law of the People’s
              Republic of China on Electronic Signature (China), PIPEDA
              (Canadian federal law), among others.
            </p>
          </div>

          <div className="w-full flex-1 "> 
            <Image
              src={`${BASE_ASSETS_URL}/v2/legality/legality.png`}
              width={10}
              height={10}
              className={`rounded-lg w-full`}
              alt="hero"
            />
            <div className="absolute top-10 right-6">
              <BasicChip images={BasicChipCertification} />
            </div>
          </div>

          <p className=" mt-8  border-t-4 border-gray-400 pt-4 md:hidden text-sm">
            ESIGN Act Sec 106 (US federal law), GPEA Sec 1710 (US federal law),
            UETA Sec 2 (US state law), eIDAS regulation (European Union),
            Electronic Transactions Act 1999 (Australia), s.7 Electronic
            Communications Act 2000 (UK), Law of the People’s Republic of China
            on Electronic Signature (China), PIPEDA (Canadian federal law),
            among others.
          </p>
        </div>
      </section> */}

      {/* legality  */}
      <section className="px-0 md:px-8  bg-cream">
        <div className=" max-w-screen-xl mx-auto px-6 xl:px-8 md:py-32 py-16 text-black md:rounded-2xl flex flex-col md:flex-row gap-6 md:gap-12 justify-between ">
          <Legality />
        </div>
      </section>

      <div
        className={`transition-colors duration-300  ${
          isDarkMode ? "bg-dark text-white" : "bg-white text-black"
        }`}
      >
        <section ref={sectionRef} className="pt-40">
          <BuilderShowCaseSection
            heading="Why BoloSign is the best solution for your business"
            // Implementation, Audit Trial, Version Control, Legal
            categories={["PDFs", "PDFs Templates", "Forms"]}
            showcaseData={showcaseData}
            isDarkMode={isDarkMode}
          />
        </section>
      </div>
      <section className="text-black px-6 md:px-8 mb-10 md:mb-28 mt-10 ">
        <h2 className="text-2xl md:text-5xl pb-4 md:pb-8 max-w-screen-lg mx-auto text-center font-bold mb-4 xl:mb-8 md:mt-12">
          Manage contracts, collect e-signatures & track progress—All in one
          platform
        </h2>
        <div className="max-w-screen-xl xl:px-8 mx-auto flex flex-col md:flex-row gap-6 justify-between items-start">
          <div className="text-center flex flex-col items-center md:gap-3 mb-6 md:mb-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/esignature-review.png`}
              alt="filter-by"
              width={300}
              height={300}
              className="w-full h-full"
            />
            <div className="text-start">
              <p className="text-xl md:text-2xl font-semibold mb-2">
                Customizable emails
              </p>
              <p className="text-lg md:text-xl">
                Edit the emails you send with signature requests to match your
                business’ tone and style, making every interaction professional.
              </p>
            </div>
          </div>

          <div className="text-center flex flex-col items-center md:gap-3 mb-6 md:mb-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/esignature-signing-order.png`}
              alt="expiry-is"
              width={300}
              height={300}
              className="w-full h-full"
            />
            <div className="text-start">
              <p className="text-xl md:text-2xl font-semibold mb-2">
                Multi-recipient signature requests
              </p>
              <p className="text-lg md:text-xl">
                Send signature requests to multiple individuals, allowing
                collaboration and simultaneous signing on a single document.
              </p>
            </div>
          </div>

          <div className="text-center flex flex-col items-center md:gap-3 mb-6 md:mb-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/esignature-service-agreement.png`}
              alt="custom-view"
              width={300}
              height={300}
              className="w-full h-full"
            />
            <div className="text-start">
              <p className="text-xl md:text-2xl font-semibold mb-2">
                Audit trial
              </p>
              <p className="text-lg md:text-xl  ">
                See a detailed record of all actions taken on a document,
                including who viewed and signed it, for your peace of mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-0 md:px-8  bg-dark text-white">
        <div className=" max-w-screen-xl mx-auto px-6 xl:px-8 md:py-32 py-16 md:rounded-2xl flex flex-col md:flex-row md:gap-12 gap-6 justify-between ">
          <div className="w-full flex-1  mb-6 md:mb-0 flex flex-col gap-6 relative">
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-semibold mb-4">
              Trusted E-Signature Platform for Enterprise Operations
            </h2>

            <ul className={`space-y-4 text-white  md:text-xl xl:text-2xl`}>
              {[
                "We understand your unique needs and challenges.",
                "Manage all signatures, contracts, and legal docs in one simple platform.",
                "Get enterprise-level features without the enterprise price tag.",
              ].map((check, idx) => (
                <li className="flex items-start text-start" key={idx}>
                  <CheckIcon className="w-5 h-5 text-gray-800 bg-purple-200 rounded-full p-1 mr-2 mt-1 shrink-0" />
                  {check}
                </li>
              ))}
            </ul>

            <div className="">
              <CLMButtonPrimary
                buttonText="Request a Demo"
                buttonUrl="/clm/request-demo"
              />
            </div>
          </div>

          <div className="w-full flex-1 ">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/esignature-enterprise.png`}
              alt="Customer discussion"
              className="w-full"
              width={1000}
              height={1000}
            />
          </div>
        </div>
      </section>

      <section className="px-0 md:px-8  ">
        <div className=" max-w-screen-xl mx-auto px-6 xl:px-8 md:pt-32 pt-16 text-black md:rounded-2xl flex flex-col md:flex-row justify-between ">
          <div className="flex-1 ">
            {showcaseData2.map((data, idx) => (
              <div
                key={idx}
                ref={(el) => (cardRefs.current[idx] = el)}
                className="scroll-mt-[130px]"
              >
                <ShowCaseCard
                  title={data.heading}
                  desc={data.subHeading}
                  imgSrc={data.imgSrc}
                  idx={idx}
                  cat={categories?.[idx]}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <section className="px-0 md:px-8  ">
        <div className=" max-w-screen-xl mx-auto px-6 xl:px-8  pt-16 text-black md:rounded-2xl flex items-center justify-center  ">
          <Image
            src={`${BASE_ASSETS_URL}/v2/clm/clm/esignature-enterprise.png`}
            alt="Customer discussion"
            className="md:w-2/3 w-full"
          />
        </div>
      </section> */}
    </div>
  );
};

export default Signature;
